import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardActions, Button,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, Alert, CircularProgress, Divider, MenuItem,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Calculate from '@mui/icons-material/Calculate';
import Build from '@mui/icons-material/Build';
import Science from '@mui/icons-material/Science';
import Bolt from '@mui/icons-material/Bolt';
import Straighten from '@mui/icons-material/Straighten';
import Thermostat from '@mui/icons-material/Thermostat';
import Speed from '@mui/icons-material/Speed';
import { toolsAPI } from '../services/api';

// Map icon name string to MUI component
const ICON_MAP = {
  Calculate, Build, Science, Bolt, Straighten, Thermostat, Speed,
};
const getIcon = (name) => {
  const Icon = ICON_MAP[name] || Calculate;
  return <Icon />;
};

const ICON_OPTIONS = Object.keys(ICON_MAP);

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'mecanica', label: 'Mecanica' },
  { value: 'electrica', label: 'Electrica' },
  { value: 'quimica', label: 'Quimica' },
  { value: 'termica', label: 'Termica' },
  { value: 'fluidos', label: 'Fluidos' },
  { value: 'materiales', label: 'Materiales' },
];

// ---------------------------------------------------------------------------
// Tool Runner Dialog — evaluates formulas dynamically
// ---------------------------------------------------------------------------
function ToolRunner({ tool, open, onClose }) {
  const cfg = tool?.config || {};
  const inputs = cfg.inputs || [];
  const outputs = cfg.outputs || [];

  const [values, setValues] = useState({});
  const [results, setResults] = useState({});

  useEffect(() => {
    if (open) {
      // Initialize with default values
      const init = {};
      inputs.forEach((inp) => { init[inp.key] = inp.default ?? ''; });
      setValues(init);
      setResults({});
    }
  }, [open]); // eslint-disable-line

  const handleChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const handleCalculate = () => {
    try {
      const res = {};
      // Build a scope with all input values as numbers
      const scope = {};
      inputs.forEach((inp) => {
        scope[inp.key] = parseFloat(values[inp.key]) || 0;
      });
      // Also add Math functions to scope
      const mathFns = 'abs,ceil,floor,round,sqrt,pow,log,log10,sin,cos,tan,PI,E,min,max,exp'.split(',');
      mathFns.forEach((fn) => { scope[fn] = Math[fn]; });

      // Evaluate each output formula
      outputs.forEach((out) => {
        try {
          // Build function args from scope keys
          const keys = Object.keys(scope);
          const vals = keys.map((k) => scope[k]);
          // eslint-disable-next-line no-new-func
          const fn = new Function(...keys, `return (${out.formula});`);
          const val = fn(...vals);
          res[out.key] = val;
          // Make output available for subsequent formulas
          scope[out.key] = val;
        } catch (e) {
          res[out.key] = 'Error: ' + e.message;
        }
      });
      setResults(res);
    } catch (e) {
      setResults({ _error: e.message });
    }
  };

  if (!tool) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getIcon(tool.icon)}
        {tool.name}
      </DialogTitle>
      <DialogContent>
        {tool.description && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>{tool.description}</Typography>
        )}

        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 1 }}>Datos de entrada</Typography>
        <Grid container spacing={2}>
          {inputs.map((inp) => (
            <Grid item xs={12} sm={6} md={4} key={inp.key}>
              <TextField
                fullWidth
                label={`${inp.label}${inp.unit ? ` (${inp.unit})` : ''}`}
                type="number"
                value={values[inp.key] ?? ''}
                onChange={(e) => handleChange(inp.key, e.target.value)}
                helperText={inp.help || ''}
                size="small"
              />
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 2, mb: 2 }}>
          <Button variant="contained" startIcon={<PlayArrow />} onClick={handleCalculate} size="large">
            Calcular
          </Button>
        </Box>

        {Object.keys(results).length > 0 && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>Resultados</Typography>
            {results._error ? (
              <Alert severity="error">{results._error}</Alert>
            ) : (
              <Grid container spacing={2}>
                {outputs.map((out) => (
                  <Grid item xs={12} sm={6} md={4} key={out.key}>
                    <Paper sx={{ p: 2, backgroundColor: '#c62828', color: '#fff', textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#fff', opacity: 0.9 }}>{out.label}</Typography>
                      <Typography variant="h5" fontWeight="bold" sx={{ color: '#fff' }}>
                        {typeof results[out.key] === 'number'
                          ? results[out.key].toLocaleString('es-ES', { maximumFractionDigits: 4 })
                          : results[out.key]}
                      </Typography>
                      {out.unit && <Typography variant="caption" sx={{ color: '#fff', opacity: 0.9 }}>{out.unit}</Typography>}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {/* Show formulas as reference */}
        {outputs.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="textSecondary">Formulas utilizadas:</Typography>
            {outputs.map((out) => (
              <Typography key={out.key} variant="caption" display="block" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                {out.label} = {out.formula}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Tool Editor Dialog
// ---------------------------------------------------------------------------
function ToolEditor({ tool, open, onClose, onSave }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('Calculate');
  const [category, setCategory] = useState('general');
  const [inputs, setInputs] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (tool) {
        setName(tool.name || '');
        setDescription(tool.description || '');
        setIcon(tool.icon || 'Calculate');
        setCategory(tool.category || 'general');
        setInputs(tool.config?.inputs || []);
        setOutputs(tool.config?.outputs || []);
      } else {
        setName('');
        setDescription('');
        setIcon('Calculate');
        setCategory('general');
        setInputs([{ key: '', label: '', unit: '', default: '', help: '' }]);
        setOutputs([{ key: '', label: '', unit: '', formula: '' }]);
      }
      setError('');
    }
  }, [open, tool]);

  const handleInputChange = (idx, field, val) => {
    setInputs((prev) => prev.map((inp, i) => i === idx ? { ...inp, [field]: val } : inp));
  };
  const handleOutputChange = (idx, field, val) => {
    setOutputs((prev) => prev.map((out, i) => i === idx ? { ...out, [field]: val } : out));
  };
  const addInput = () => setInputs([...inputs, { key: '', label: '', unit: '', default: '', help: '' }]);
  const removeInput = (idx) => setInputs(inputs.filter((_, i) => i !== idx));
  const addOutput = () => setOutputs([...outputs, { key: '', label: '', unit: '', formula: '' }]);
  const removeOutput = (idx) => setOutputs(outputs.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!name.trim()) { setError('El nombre es obligatorio'); return; }
    const validInputs = inputs.filter((i) => i.key && i.label);
    const validOutputs = outputs.filter((o) => o.key && o.label && o.formula);
    if (validInputs.length === 0) { setError('Define al menos un campo de entrada'); return; }
    if (validOutputs.length === 0) { setError('Define al menos un resultado con formula'); return; }
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || null,
        icon,
        category,
        config: { inputs: validInputs, outputs: validOutputs },
      });
      onClose();
    } catch (e) {
      setError('Error al guardar: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{tool ? 'Editar Herramienta' : 'Nueva Herramienta'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Nombre" value={name} onChange={(e) => setName(e.target.value)} required />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth select label="Icono" value={icon} onChange={(e) => setIcon(e.target.value)}>
              {ICON_OPTIONS.map((i) => <MenuItem key={i} value={i}>{i}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth select label="Categoria" value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORY_OPTIONS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Descripcion" value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={2} />
          </Grid>
        </Grid>

        {/* Inputs */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
          Campos de Entrada (variables)
        </Typography>
        <Typography variant="caption" color="textSecondary" gutterBottom display="block">
          Define las variables que el usuario introducira. La "clave" es el nombre de variable usado en las formulas (sin espacios).
        </Typography>
        {inputs.map((inp, idx) => (
          <Grid container spacing={1} key={idx} sx={{ mb: 1 }} alignItems="center">
            <Grid item xs={2}>
              <TextField size="small" fullWidth label="Clave" value={inp.key} placeholder="ej: sigma_y"
                onChange={(e) => handleInputChange(idx, 'key', e.target.value.replace(/\s/g, '_'))} />
            </Grid>
            <Grid item xs={3}>
              <TextField size="small" fullWidth label="Etiqueta" value={inp.label} placeholder="ej: Limite elastico"
                onChange={(e) => handleInputChange(idx, 'label', e.target.value)} />
            </Grid>
            <Grid item xs={1.5}>
              <TextField size="small" fullWidth label="Unidad" value={inp.unit} placeholder="MPa"
                onChange={(e) => handleInputChange(idx, 'unit', e.target.value)} />
            </Grid>
            <Grid item xs={1.5}>
              <TextField size="small" fullWidth label="Defecto" value={inp.default} type="number"
                onChange={(e) => handleInputChange(idx, 'default', e.target.value)} />
            </Grid>
            <Grid item xs={3}>
              <TextField size="small" fullWidth label="Ayuda" value={inp.help} placeholder="Texto de ayuda"
                onChange={(e) => handleInputChange(idx, 'help', e.target.value)} />
            </Grid>
            <Grid item xs={1}>
              <IconButton size="small" color="error" onClick={() => removeInput(idx)}><DeleteIcon fontSize="small" /></IconButton>
            </Grid>
          </Grid>
        ))}
        <Button size="small" startIcon={<Add />} onClick={addInput}>Anadir entrada</Button>

        {/* Outputs */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
          Resultados (formulas)
        </Typography>
        <Typography variant="caption" color="textSecondary" gutterBottom display="block">
          Define los calculos. En "Formula" usa las claves de entrada y funciones matematicas (sqrt, pow, PI, etc.).
          Los resultados anteriores tambien estan disponibles como variables en formulas posteriores.
        </Typography>
        {outputs.map((out, idx) => (
          <Grid container spacing={1} key={idx} sx={{ mb: 1 }} alignItems="center">
            <Grid item xs={2}>
              <TextField size="small" fullWidth label="Clave" value={out.key} placeholder="ej: tension"
                onChange={(e) => handleOutputChange(idx, 'key', e.target.value.replace(/\s/g, '_'))} />
            </Grid>
            <Grid item xs={3}>
              <TextField size="small" fullWidth label="Etiqueta" value={out.label} placeholder="ej: Tension maxima"
                onChange={(e) => handleOutputChange(idx, 'label', e.target.value)} />
            </Grid>
            <Grid item xs={1.5}>
              <TextField size="small" fullWidth label="Unidad" value={out.unit} placeholder="MPa"
                onChange={(e) => handleOutputChange(idx, 'unit', e.target.value)} />
            </Grid>
            <Grid item xs={4.5}>
              <TextField size="small" fullWidth label="Formula" value={out.formula} placeholder="ej: sigma_y / factor_seguridad"
                onChange={(e) => handleOutputChange(idx, 'formula', e.target.value)}
                sx={{ '& input': { fontFamily: 'monospace' } }} />
            </Grid>
            <Grid item xs={1}>
              <IconButton size="small" color="error" onClick={() => removeOutput(idx)}><DeleteIcon fontSize="small" /></IconButton>
            </Grid>
          </Grid>
        ))}
        <Button size="small" startIcon={<Add />} onClick={addOutput}>Anadir resultado</Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main Tools Page
// ---------------------------------------------------------------------------
export default function Tools() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [runnerTool, setRunnerTool] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadTools = async () => {
    try {
      setLoading(true);
      const resp = await toolsAPI.list();
      setTools(resp.data);
    } catch { setTools([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadTools(); }, []);

  const handleCreate = () => { setEditingTool(null); setEditorOpen(true); };
  const handleEdit = (t) => { setEditingTool(t); setEditorOpen(true); };

  const handleSave = async (data) => {
    if (editingTool) {
      await toolsAPI.update(editingTool.id, data);
    } else {
      await toolsAPI.create(data);
    }
    loadTools();
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await toolsAPI.delete(deleteConfirm.id);
      loadTools();
    } catch (e) {
      setError('Error al eliminar la herramienta');
    }
    setDeleteConfirm(null);
  };

  // Group by category
  const grouped = useMemo(() => {
    const map = {};
    tools.forEach((t) => {
      const cat = t.category || 'general';
      if (!map[cat]) map[cat] = [];
      map[cat].push(t);
    });
    return map;
  }, [tools]);

  const catLabel = (key) => CATEGORY_OPTIONS.find((c) => c.value === key)?.label || key;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">Herramientas</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Nueva Herramienta
        </Button>
      </Box>

      {tools.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Build sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No hay herramientas creadas
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Crea calculadoras y herramientas de ingenieria personalizadas.
          </Typography>
          <Button variant="outlined" startIcon={<Add />} onClick={handleCreate}>
            Crear primera herramienta
          </Button>
        </Paper>
      ) : (
        Object.entries(grouped).map(([cat, catTools]) => (
          <Box key={cat} sx={{ mb: 3 }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {catLabel(cat)}
            </Typography>
            <Grid container spacing={2}>
              {catTools.map((t) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={t.id}>
                  <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box sx={{ color: 'primary.main' }}>{getIcon(t.icon)}</Box>
                        <Typography variant="subtitle1" fontWeight="bold">{t.name}</Typography>
                      </Box>
                      {t.description && (
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                          {t.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip size="small" label={`${(t.config?.inputs || []).length} entradas`} variant="outlined" />
                        <Chip size="small" label={`${(t.config?.outputs || []).length} resultados`} variant="outlined" />
                      </Box>
                    </CardContent>
                    <CardActions>
                      <Button size="small" startIcon={<PlayArrow />} onClick={() => setRunnerTool(t)} variant="contained">
                        Usar
                      </Button>
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEdit(t)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => setDeleteConfirm(t)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}

      {/* Runner */}
      <ToolRunner tool={runnerTool} open={!!runnerTool} onClose={() => setRunnerTool(null)} />

      {/* Editor */}
      <ToolEditor tool={editingTool} open={editorOpen} onClose={() => setEditorOpen(false)} onSave={handleSave} />

      {/* Delete confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="sm">
        <DialogTitle>Eliminar herramienta</DialogTitle>
        <DialogContent>
          <Typography>
            Vas a eliminar la herramienta <strong>{deleteConfirm?.name}</strong>. Esta accion no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
